import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { directoryApi, type ProjectContactEntry, type ProjectDirectory } from '@/lib/apiClient';
import { Button } from '@/components';
import AddContactDialog from './AddContactDialog';
import EditContactDialog from './EditContactDialog';
import { DirectoryStyles } from './directoryStyles';

/**
 * A job's Contacts tab (#598, step 1): who is on the job. Our own team, from the
 * job's existing PM / PE / estimator / safety manager slots and its field
 * leaders; then the outside people — owner, design team, subs, suppliers —
 * from the company-wide address book, grouped by their role on this job.
 *
 * Everyone who can see the job sees this; those who can edit jobs add people,
 * change their role, fix their details and take them off. A fix to a person or
 * a company is to the address book, so it shows on every job they're on.
 */
export default function ContactsTab({ projectId }: { projectId: number }) {
  const qc = useQueryClient();
  const key = ['project-contacts', projectId];
  const q = useQuery({ queryKey: key, queryFn: () => directoryApi.forProject(projectId) });
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ProjectContactEntry | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  /**
   * A write answers with the job's directory again: use it, no second read. A
   * failed removal's message goes with it, rather than staying above a list
   * that has moved on (review).
   */
  const took = ({ team, contacts }: Pick<ProjectDirectory, 'team' | 'contacts'>) => {
    setRemoveError(null);
    qc.setQueryData<ProjectDirectory>(key, (old) => (old ? { ...old, team, contacts } : old));
  };

  const data = q.data;
  const divisionTitle = (c: string) => data?.divisions.find((d) => d.code === c)?.title ?? c;

  const groups = useMemo(() => {
    if (!data) return [];
    const term = filter.trim().toLowerCase();
    const shown = term
      ? data.contacts.filter((c) => [c.person.name, c.person.title, c.person.email, c.company?.name, data.roles.find((r) => r.key === c.role)?.label]
        .some((v) => (v ?? '').toLowerCase().includes(term)))
      : data.contacts;
    return data.roles
      .map((r) => ({ role: r, entries: shown.filter((c) => c.role === r.key) }))
      .filter((g) => g.entries.length);
  }, [data, filter]);

  const remove = async (c: ProjectContactEntry) => {
    if (!window.confirm(`Take ${c.person.name} off this job? They stay in the address book.`)) return;
    setRemoveError(null);
    try {
      took(await directoryApi.remove(projectId, c.id));
    } catch (e) {
      setRemoveError((e as Error).message || 'They weren’t taken off. Try again.');
      // Refused because the list moved on: show the job as it is now (review).
      void qc.invalidateQueries({ queryKey: key });
    }
  };

  if (q.isPending) return <p className="dir-muted">Loading the job’s contacts…</p>;
  if (q.isError || !data) return <div className="dir-error" role="alert">Couldn’t load the job’s contacts. {(q.error as Error | null)?.message}</div>;

  return (
    <div className="dir">
      <DirectoryStyles />
      <div className="pw-card">
        <h2 className="pw-card-hd">Our team</h2>
        {data.team.length === 0 ? (
          <p className="dir-pad dir-muted">Nobody is assigned yet. The team is set on the job’s Overview.</p>
        ) : (
          <div role="table" aria-label="Our team">
            {data.team.map((t) => (
              <div key={`${t.role}:${t.userId}`} role="row" className="dir-row team">
                <span role="cell" className="dir-role">{t.role}</span>
                <span role="cell" className="dir-name">{t.name ?? '—'}</span>
                <span role="cell">{t.email ? <a href={`mailto:${t.email}`}>{t.email}</a> : <span className="dir-muted">—</span>}</span>
                <span role="cell">{t.phone ? <a href={`tel:${t.phone}`}>{t.phone}</a> : <span className="dir-muted">—</span>}</span>
              </div>
            ))}
          </div>
        )}
        <p className="dir-pad dir-muted dir-small">Our team comes from the job’s Overview (project managers, engineer, estimator, safety manager) and its field leaders, and is changed there.</p>
      </div>

      <div className="pw-card" style={{ marginTop: 'var(--ew-s-4)' }}>
        <div className="dir-hd">
          <h2 className="pw-card-hd" style={{ border: 0 }}>Contacts</h2>
          <input className="dir-filter" type="search" placeholder="Filter by name, company, email…" aria-label="Filter contacts"
            value={filter} onChange={(e) => setFilter(e.target.value)} />
          {data.canEdit && <Button size="sm" onClick={() => setAdding(true)}>＋ Add Contact</Button>}
        </div>
        {removeError && <div className="dir-error dir-pad" role="alert">{removeError}</div>}
        {data.contacts.length === 0 ? (
          <p className="dir-pad dir-muted">
            No outside contacts on this job yet.{data.canEdit ? ' Add the owner, the design team and the subs with Add Contact.' : ''}
          </p>
        ) : groups.length === 0 ? (
          <p className="dir-pad dir-muted">Nobody matches “{filter.trim()}”.</p>
        ) : groups.map((g) => (
          <section key={g.role.key} aria-label={g.role.label}>
            <div className="dir-group">{g.role.label}</div>
            {g.entries.map((c) => (
              <div key={c.id} className="dir-row">
                <span className="dir-co">
                  {c.company ? c.company.name : <span className="dir-muted">No company</span>}
                  {/* The office's phone and town, when the book has them: otherwise only Edit showed them. */}
                  {c.company && (c.company.phone || c.company.city || c.company.state) && (
                    <span className="dir-co-meta">
                      {c.company.phone && <a href={`tel:${c.company.phone}`}>{c.company.phone} <span className="dir-muted">office</span></a>}
                      {(c.company.city || c.company.state) && (
                        <span className="dir-muted">{[c.company.city, c.company.state].filter(Boolean).join(', ')}</span>
                      )}
                    </span>
                  )}
                  {c.company && c.company.divisions.length > 0 && (
                    <span className="dir-divs">
                      {c.company.divisions.map((d) => <span key={d} className="dir-div" title={divisionTitle(d)}>{d}</span>)}
                    </span>
                  )}
                </span>
                <span>
                  <span className="dir-name">{c.person.name}</span>
                  {c.person.title && <span className="dir-muted"> · {c.person.title}</span>}
                  {c.note && <span className="dir-note">{c.note}</span>}
                </span>
                <span>{c.person.email ? <a href={`mailto:${c.person.email}`}>{c.person.email}</a> : <span className="dir-muted">—</span>}</span>
                <span className="dir-phones">
                  {c.person.phone && <a href={`tel:${c.person.phone}`}>{c.person.phone}</a>}
                  {c.person.cell && <a href={`tel:${c.person.cell}`}>{c.person.cell} <span className="dir-muted">cell</span></a>}
                  {!c.person.phone && !c.person.cell && <span className="dir-muted">—</span>}
                </span>
                <span className="dir-acts">
                  {data.canEdit && (
                    <>
                      <button type="button" className="dir-act" onClick={() => setEditing(c)} aria-label={`Edit ${c.person.name}`}>Edit</button>
                      <button type="button" className="dir-act" onClick={() => { void remove(c); }} aria-label={`Take ${c.person.name} off the job`}>Remove</button>
                    </>
                  )}
                </span>
              </div>
            ))}
          </section>
        ))}
      </div>

      {adding && (
        <AddContactDialog projectId={projectId} directory={data} onClose={() => setAdding(false)}
          onAdded={(d) => { took(d); setAdding(false); }} />
      )}
      {editing && (
        <EditContactDialog projectId={projectId} directory={data} entry={editing} onClose={() => setEditing(null)}
          onSaved={(d) => { took(d); setEditing(null); }} />
      )}
    </div>
  );
}
